import React, { useEffect, useMemo, useState } from 'react';
import { HeadlineLarge, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import TextField from '@shared/ui/atoms/TextField';
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import styled from "styled-components";
import { isNumber } from "@shared/utils/TypeUtils";
import {
  failureNotification,
  successNotification,
  warningNotification
} from "@shared/store/notification.duck";
import { getMeetingRoomById, updateMeetingRoom } from "@features/meeting-rooms/MeetingRoomsService";
import { MeetingRoom, MeetingRoomFacilities } from "@features/meeting-rooms/pages/models/MeetingRooms";
import { OfficeDropDownList } from '@shared/ui/organisms/OfficeDropDownList';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { useParams } from "react-router";
import { findOfficeInOffices } from "@shared/utils/OfficeHelper";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { selectAllOfficesForMeetingRooms, selectOffices } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { setMeetingRoomFacilities } from "@features/meeting-rooms/pages/models/room-finder.duck";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import { OutlookMeetingRoomLookup } from "./OutlookMeetingRoomLookup";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function EditMeetingRoomPage(props: Props) {
  const dispatch = useDispatch();
  const params: any = useParams();
  const [meetingRoom, setMeetingRoom] = useState<MeetingRoom | undefined>(undefined);
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [tv, setTv] = useState(false);
  const [confCall, setConfCall] = useState(false);
  const [whiteboard, setWhiteboard] = useState(false);
  const [chargePoints, setChargePoints] = useState(false);
  const [microsoftRoomId, setMicrosoftRoomId] = useState('');
  const [office, setOffice] = useState<OfficeEntity | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();
  const offices = useSelector(selectOffices);
  const [activeRequest, setActiveRequest] = useState(false);
  const allOfficesForMeetingRooms = useSelector(selectAllOfficesForMeetingRooms);

  const meetingRoomId = useMemo(() => params.meetingRoomId, [params]);

  useEffect(() => {
    if (meetingRoom || loading) return;

    if (meetingRoomId && allOfficesForMeetingRooms.length > 0 && !activeRequest) {
      setLoading(true);
      setActiveRequest(activeRequest);
      getMeetingRoomById(meetingRoomId).then((resp: MeetingRoom) => {
        setMeetingRoom(resp);
        setName(resp.name);
        setCapacity(resp.capacity.toString());
        setMicrosoftRoomId(resp.microsoftRoomId || '');
        setMeetingRoomFacilities(resp.facilities)

        setTv(resp.facilities.includes(MeetingRoomFacilities.TV));
        setConfCall(resp.facilities.includes(MeetingRoomFacilities.ConferenceCall));
        setWhiteboard(resp.facilities.includes(MeetingRoomFacilities.WhiteBoard));
        setChargePoints(resp.facilities.includes(MeetingRoomFacilities.ChargePoints));

        const office = findOfficeInOffices(allOfficesForMeetingRooms, resp.officeId);
        if (office) {
          setOffice(office);
        } else {
          dispatch(failureNotification('Cannot load meeting room, failed to find office'));
          throw new Error('Cannot load meeting room, failed to find office');
        }
      })
        .finally(() => setLoading(false));
    }
  }, [activeRequest, dispatch, offices, meetingRoomId, meetingRoom, loading, allOfficesForMeetingRooms]);


  const setCapacityWithCheck = (val: string) => {
    if (isNumber(val)) {
      setCapacity(val);
    }
  }

  const save = async () => {
    if (!capacity) {
      dispatch(failureNotification('Capacity needs to be set'));
      return;
    }

    if (!name) {
      dispatch(failureNotification('Name needs to be set'));
      return;
    }

    if (!office) {
      dispatch(failureNotification('Select an office'));
      return;
    }

    if (!meetingRoom) {
      dispatch(failureNotification('No meeting room to update'));
      return;
    }

    try {
      setLoading(true);
      let facilities: MeetingRoomFacilities[] = [];

      if (confCall) facilities.push(MeetingRoomFacilities.ConferenceCall);
      if (tv) facilities.push(MeetingRoomFacilities.TV);
      if (chargePoints) facilities.push(MeetingRoomFacilities.ChargePoints);
      if (whiteboard) facilities.push(MeetingRoomFacilities.WhiteBoard);

      meetingRoom.name = name;
      meetingRoom.capacity = parseInt(capacity);
      meetingRoom.officeId = office.id;
      meetingRoom.facilities = facilities;
      meetingRoom.microsoftRoomId = microsoftRoomId;

      // @ts-ignore
      await updateMeetingRoom(meetingRoom);
      dispatch(successNotification("Meeting room updated"));

    } finally {
      setLoading(false);
    }
  }

  const onDeleteMeetingRoomClick = () => {
    if (!meetingRoom) {
      dispatch(warningNotification('No meeting room to delete'));
      return;
    }
    dispatch(openDialogWithPayload({
      payload: { meetingRoom: meetingRoom },
      activeDialog: DialogIdentifiers.DeleteMeetingRoomConfirm
    }));
  }

  return (
    <>
      <GoBackButton url={meetingRoom?.officeId ? `/manage/offices/${meetingRoom.officeId}?panel=3` : '/manage/offices'} />
      <HeadlineLarge style={{marginBottom: 16}}>{t('settings.meeting-rooms.edit')}</HeadlineLarge>
      <TextField disabled={loading}
                 value={name}
                 onChange={setName}
                 fullWidth={true}
                 label={"settings.meeting-rooms.name"} />
      <TextField disabled={loading}
                 value={capacity}
                 onChange={setCapacityWithCheck}
                 fullWidth={true}
                 label={"settings.meeting-rooms.capacity"} />

      <OutlookMeetingRoomLookup loading={loading}
                                microsoftRoomId={microsoftRoomId}
                                onChange={roomId => setMicrosoftRoomId(roomId)} />

      <BodyRegular weight={600} style={{marginTop: 24}} colour={Colours.darkGrey}>{t('settings.meeting-rooms.office')}</BodyRegular>
      <OfficeDropDownList onSelect={setOffice} disabled={loading} preSelectedOffice={office?.id ?? meetingRoom?.officeId} offices={allOfficesForMeetingRooms} />


      <HeadlineSmall style={{marginTop: 24}}>{t('room-finder.facilities.title')}</HeadlineSmall>
      <FacilitiesList>
        <Checkbox disabled={loading} value={tv} onChange={setTv} text={'room-finder.facilities.tv'} />
        <Checkbox disabled={loading} value={confCall} onChange={setConfCall} text={'room-finder.facilities.conference-call'} />
        <Checkbox disabled={loading} value={whiteboard} onChange={setWhiteboard} text={'room-finder.facilities.whiteboard'} />
        <Checkbox disabled={loading} value={chargePoints} onChange={setChargePoints} text={'room-finder.facilities.charge-points'} />
      </FacilitiesList>
      <PrimaryButton disabled={loading} fullWidth={true} size={ButtonSize.Large} style={{marginTop: 24, marginBottom: 16}} click={save}
                     text={'button.update'} />

      <OutlineButton disabled={loading} fullWidth={true} size={ButtonSize.Large} click={onDeleteMeetingRoomClick} text={'delete-meeting-room-dialog.delete-meeting-room'} />
    </>
  )
}

interface Props {
}

const FacilitiesList = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  & > * {
    margin-bottom: 12px;
  }
`
